<template>
  <div ref="trashContainer" v-outside-click="closeTrashBox" class="kk-subnav__trash">
    <div class="kk-subnav__sub">
      <div data-test-id="subnav-actions" class="kk-subnav__header">
        <span class="kk-subnav__header--text">{{ $t("sidebar.trash.Trash") }}</span>

        <span v-if="selectedItems.length > 0" class="kk-subnav__header--amount"
          >{{ selectedItems.length }}/{{ trashItems.length }}</span
        >
        <span style="display: flex; flex-grow: 1"></span>

        <i class="icon-close" @click="closeTrashBox"></i>
      </div>
      <div data-test-id="subnav-actions" class="kk-subnav__actions">
        <span data-test-id="selectAll-in-trash" class="kk-subnav__actions-selectall" @click="selectAll">{{
          $t("sidebar.trash.selectAll")
        }}</span>
        <span style="display: flex; flex-grow: 1"></span>
        <span
          v-if="selectedItems.length > 0"
          data-test-id="delete-permenantly-icon"
          class="kk-subnav__actions-icon"
          :class="icons.Trash"
          @click="deletePermenantly"
        ></span>
        <span
          v-if="selectedItems.length > 0"
          data-test-id="restore-icon"
          class="kk-subnav__actions-icon"
          :class="icons.Restore"
          @click="restore"
        ></span>
      </div>
      <div
        v-if="trashItems.length > 0"
        ref="trashBoxContent"
        class="kk-subnav__content"
        :class="{ expanded: isExpanded }"
      >
        <div v-if="isExpanded & (trashItems.length > 5)" class="kk-subnav__scrollup" @click="moveItemsUp">
          <i class="icon-menu-up"></i>
        </div>
        <div v-for="(item, i) in trashItems" :key="item.id" @click="select($event, i, item.id)">
          <div
            class="kk-subnav__trash-item"
            :class="{
              'kk-subnav__trash-item-active': selectedItems.includes(item.id),
              'kk-subnav__trash-item-moveItem': i == index,
            }"
          >
            <div class="kk-subnav__trash-item-icon">
              <span :class="icons[item.type]"></span>
            </div>
            <div class="kk-subnav__trash-item-title">
              <span
                class="kk-subnav__trash-item-title-path"
                :title="Array.isArray(item.path) ? item.path.join(' > ') : ''"
                >{{ getItemPath(item) }}</span
              >
              <span>
                {{ item.title }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="kk-subnav__trash-empty">{{ $t("sidebar.trash.emptyMessage") }}</div>
      <div
        v-if="trashItems.length > 0"
        class="kk-subnav__scrolldown"
        @mouseenter="handleMouseEnter"
        @click="moveItemsDown"
      >
        <i class="icon-menu-down"></i>
      </div>
    </div>
  </div>
</template>

<script>
import { icons } from "../../../utils/icons";
import { navigateTo } from "../../../router";
import hotkeys from "hotkeys-js";
import { HotkeyScopes } from "../../../utils/constants";

export default {
  props: ["trashItems", "store"],
  data() {
    return {
      icons,
      index: 0,
      previousScope: null,
      isExpanded: null,
      oldIndex: null,
    };
  },
  computed: {
    selectedItems: {
      get() {
        if (!this.store) return [];
        return this.$store.state[this.store].selectedTrashItems;
      },
      set(newValue) {
        if (!this.store) return false;
        return this.$store.dispatch(`${this.store}/setSelectedTrashItems`, newValue);
      },
    },
    checkAll() {
      if (this.selectedItems.length > 0) {
        if (this.selectedItems.length === this.trashItems.length) return icons.Check;
        else return icons.OutlinedCircleMinus;
      }
      return icons.OutlinedCheck;
    },
    deleteType() {
      switch (this.store) {
        case "players":
          return {
            title: this.$t("sidebar.delete.player.title"),
            description: this.$t("sidebar.delete.player.description"),
            itemType: "players",
          };
        case "processes":
          return {
            title: this.$t("sidebar.delete.process.title"),
            description: this.$t("sidebar.delete.process.description"),
            itemType: "processes",
          };
        case "grids":
          return {
            title: this.$t("sidebar.delete.grid.title"),
            description: this.$t("sidebar.delete.grid.description"),
            itemType: "processes",
          };
        case "businesses":
          return {
            title: this.$t("sidebar.delete.business.title"),
            description: this.$t("sidebar.delete.business.description"),
            itemType: "processes",
          };
        case "teams":
          return {
            title: this.$t("sidebar.delete.team.title"),
            description: this.$t("sidebar.delete.team.description"),
            itemType: "teams",
          };
        case "sheets":
          return {
            title: this.$t("sidebar.delete.sheet.title"),
            description: this.$t("sidebar.delete.sheet.description"),
            itemType: "sheets",
          };
        // Add more cases as needed
        default:
          return null; // or default action
      }
    },
  },
  mounted() {
    this.setScope();
  },
  beforeUnmount() {
    this.$store.dispatch("setScope", this.previousScope);
    hotkeys.unbind("up, down,space, delete,ctrl+r,ctrl+a,ctrl+up, ctrl+down, ctrl+space", HotkeyScopes.Trash);
  },

  methods: {
    handleMouseEnter() {
      if (this.trashItems.length > 5) {
        this.isExpanded = true;
      }
    },
    closeTrashBox() {
      if (!this.store) return false;
      this.selectedItems = [];
      return this.$store.dispatch(`${this.store}/toggleTrash`, false);
    },

    getItemPath(item) {
      let formattedPath;
      const pathParts = item.path; // Expect `path` as an array of parts

      if (!Array.isArray(pathParts)) {
        return "";
      }

      if (pathParts.length <= 3) {
        // If there are 3 or fewer items, include them all
        formattedPath = pathParts.join(" > ");

        // If the path is longer than 45 characters, truncate it
        if (formattedPath.length > 45) {
          formattedPath = `...${formattedPath.slice(-45)}`;
        }
      } else {
        // Include the first, "..." for the middle, and the last two
        const first = pathParts[0];
        const lastTwo = pathParts.slice(-2).join(" > ");
        formattedPath = `${first} > ... > ${lastTwo}`;
      }

      return formattedPath;
    },
    moveItemsUp() {
      const container = this.$refs.trashBoxContent;
      container.scrollBy({
        top: 141,
        behavior: "smooth",
      });
    },
    moveItemsDown() {
      const container = this.$refs.trashBoxContent;
      container.scrollBy({
        top: -141,
        behavior: "smooth",
      });
    },
    setScope() {
      this.previousScope = hotkeys.getScope();
      this.$store.dispatch("setScope", HotkeyScopes.Trash);
      // Below line is necessary
      hotkeys.unbind("up, down, space, delete,ctrl+r,ctrl+a", HotkeyScopes.Trash);
      hotkeys("down", HotkeyScopes.Trash, this.moveDown);
      hotkeys("up", HotkeyScopes.Trash, this.moveUp);
      hotkeys("delete", HotkeyScopes.Trash, this.deletePermenantly);
      hotkeys("ctrl+r", HotkeyScopes.Trash, this.restore);
      hotkeys("ctrl+a", HotkeyScopes.Trash, this.selectAll);
      hotkeys("ctrl+up", HotkeyScopes.Trash, this.navigateUp);
      hotkeys("ctrl+down", HotkeyScopes.Trash, this.navigateDown);
      hotkeys("ctrl+space", HotkeyScopes.Trash, this.toggleSelect);
      hotkeys("shift+space", HotkeyScopes.Trash, this.selectRange);
    },
    selectRange() {
      this.handleRangeSelection(this.index === -1 ? 0 : this.index, this.oldIndex);
    },
    navigateUp(event) {
      event.preventDefault();
      if (this.index > 0) {
        this.index = this.index <= 0 ? this.trashItems.length - 1 : (this.index - 1) % this.trashItems.length;
        this.scrollToIndex();
      }
      if (this.selectedItems.length == this.trashItems.length) {
        this.selectedItems = [];
      }
    },

    navigateDown(event) {
      if (this.index < this.trashItems.length - 1) {
        this.index = (this.index + 1) % this.trashItems.length;
        this.scrollToIndex();
      }
      if (this.selectedItems.length == this.trashItems.length) {
        this.selectedItems = [];
      }
    },

    toggleSelect(event) {
      event.preventDefault();
      if (this.index >= 0 && this.trashItems.length > 0) {
        const itemId = this.trashItems[this.index]?.id;
        if (itemId) this.select(event, this.index, itemId);
      }
    },
    scrollToIndex() {
      const trash = this.$refs.trashBoxContent;
      if (trash) {
        const items = trash.querySelectorAll(".kk-subnav__trash-item");
        if (items && items[this.index]) {
          items[this.index].scrollIntoView({
            behavior: "auto", // Smooth scrolling for a better user experience
            block: "center", // Scroll the item into the nearest visible position
          });
        }
      }
    },
    moveUp(event) {
      event.preventDefault();
      if (this.index > 0) {
        this.index = this.index <= 0 ? this.trashItems.length - 1 : (this.index - 1) % this.trashItems.length;
        this.select(event, this.index, this.trashItems[this.index]?.id);
        this.oldIndex = this.index;
        this.scrollToIndex();
      }
      if (this.selectedItems.length == this.trashItems.length) {
        this.selectedItems = [];
      }
    },
    moveDown(event) {
      event.preventDefault();
      if (this.index < this.trashItems.length - 1) {
        this.index = (this.index + 1) % this.trashItems.length;
        this.select(event, this.index, this.trashItems[this.index]?.id);
        this.oldIndex = this.index;
        this.scrollToIndex();
      }
      if (this.selectedItems.length == this.trashItems.length) {
        this.selectedItems = [];
      }
    },
    restore(e) {
      e.preventDefault();
      this.$store.dispatch(`${this.store}/restore`, { ids: this.selectedItems });
      this.selectedItems = [];
      this.index = 0;
    },

    deletePermenantly() {
      if (!this.selectedItems.length > 0) return;
      this.$store.dispatch("panel/setDeleteDialog", {
        ids: this.selectedItems,
        show: true,
        ...this.deleteType,
        action: async (action) => {
          await this.$store.dispatch(`${this.store}/remove`, { ids: this.selectedItems });
          this.selectedItems = [];
          this.index = 0;
        },
      });
    },

    select(event, idx, id) {
      if (event.shiftKey) {
        this.handleRangeSelection(this.index == -1 ? 0 : this.index, idx);
        return;
      } else if (event.ctrlKey || event.metaKey) {
        // Add or remove items when Ctrl/Cmd is pressed
        if (this.selectedItems.includes(id)) {
          // If already selected, deselect it
          this.selectedItems = this.selectedItems.filter((item) => item !== id);
        } else {
          // Otherwise, add it to the selection
          this.selectedItems = [...this.selectedItems, id];
        }
      } else {
        // Default behavior: select only the clicked item
        this.selectedItems = [id];
      }
      this.oldIndex = idx;
      this.index = idx;
    },

    handleRangeSelection(startIndex, endIndex) {
      if (startIndex > endIndex) {
        [startIndex, endIndex] = [endIndex, startIndex];
      }
      // Clear previously selected items if shift-clicking a new range
      this.selectedItems = [];

      // Select the range between the last selected item and the current item
      for (let i = startIndex; i <= endIndex; i++) {
        const itemId = this.trashItems[i]?.id;
        if (!this.selectedItems.includes(itemId)) {
          this.selectedItems.push(itemId);
        }
      }
    },
    selectAll() {
      if (this.selectedItems.length === this.trashItems.length) {
        this.selectedItems = [];
      } else if (this.selectedItems.length > 0 || this.selectedItems.length === 0) {
        this.selectedItems = this.trashItems.map((item) => item.id);
      }
    },
    check(itemId) {
      const exist = this.selectedItems.includes(itemId);
      return exist ? icons.Check : icons.OutlinedCircle;
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/styles/variables";

.kk-subnav {
  &__sub {
    position: relative;
  }

  &__scrolldown,
  &__scrollup {
    position: sticky;
    bottom: 0;
    width: 100%;
    text-align: center;
    z-index: 100;
    background: #fff;
    i {
      font-size: 2rem;
    }
  }

  &__scrollup {
    top: 0;
  }

  &__scrolldown {
    position: absolute;
    bottom: 0;
  }

  &__actions {
    &-selectall {
      cursor: pointer;
    }
  }

  &__actions,
  &__header {
    display: flex;
    gap: 1rem;
    padding: 10px;
    align-items: center;
    height: 35px;
    border-bottom: 3px solid #d7d7d7;
    font-size: 14px;

    &-icon {
      font-size: 17px;
      color: @kk-color-primary-blue;
      cursor: pointer;
      display: flex;
      gap: 1rem;
    }
  }

  &__header {
    color: @kk-corner-header-color;
    height: 40px;
    font-size: 18px;

    &--amount {
      font-size: 10px;
      letter-spacing: 2px;
    }
    i {
      cursor: pointer;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-bottom: 3rem;
    max-height: 16.8rem; /* Initial height */
    overflow: auto;
    transition: max-height 0.5s ease-in-out; /* Smooth transition for max-height */

    &.expanded {
      max-height: calc(100vh - 13.8rem);
    }
    /* Hide the scrollbar for Internet Explorer, Edge and Firefox */
    -ms-overflow-style: none; /* Internet Explorer and Edge */
    scrollbar-width: none; /* Firefox */

    /* Hide the scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__trash {
    position: absolute;
    left: 101%;
    z-index: 100;
    background: #fff;
    bottom: 0px;
    width: 100%;
    min-height: 150px;
    box-shadow:  /* Right shadow */ 2px 0px 5px rgba(0, 0, 0, 0.2), /* Bottom shadow */ 0px 2px 5px rgba(0, 0, 0, 0.2);

    &-item {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 5px 10px;
      height: 45px;
      cursor: pointer;
      color: @kk-color-primary-blue;
      overflow: hidden;
      white-space: nowrap;

      &:not(&-active)&:hover {
        background-color: @kk-color-light-blue;
      }

      &-moveItem {
        background-color: @kk-color-light-blue;
      }

      &-active {
        background-color: @kk-color-primary-blue;
        .kk-subnav__trash-item-title,
        .kk-subnav__trash-item-icon {
          color: @kk-color-white;
        }
      }

      &-icon {
        font-size: 18px;
      }

      &-title {
        font-size: 14px;
        gap: 0.6rem;
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        align-items: start;
        gap: 0;

        &-path {
          font-size: 10px;
        }
      }
    }

    &-empty {
      text-align: center;
      padding: 2rem 0;
      color: @kk-color-gray;
      font-size: 14px;
    }
  }
}
</style>
